import React, { FunctionComponent, ReactNode, useCallback } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import styles from "./NewsRealestatesSection.module.css";

type NewsRealestatesSectionProps = {
    realestates: ISelectableRealestate[];
    onChange: (realestates: ISelectableRealestate[]) => void;
    // Shown below the chips, e.g. the button to add realestates. Stays visible when the chips scroll.
    children?: ReactNode;
};

const NewsRealestatesSectionBase: FunctionComponent<NewsRealestatesSectionProps> = ({
    realestates,
    onChange,
    children
}) => {
    const { t } = useTranslation();

    // A news always needs at least one realestate.
    const canRemoveRealestate = realestates.length > 1;

    const onRemoveRealestate = useCallback(
        (removedRealestate: ISelectableRealestate) => () => {
            onChange(realestates.filter((realestate) => realestate.id !== removedRealestate.id));
        },
        [onChange, realestates]
    );

    return (
        <div>
            <div className={styles.Chips}>
                {realestates.length === 0 && (
                    <span className={styles.Empty}>{t("screens.realestate.news_edit.no_realestates_selected")}</span>
                )}
                {realestates.map((realestate) => (
                    <span key={realestate.id} className={styles.Chip} title={realestate.number}>
                        <span className={styles.ChipName}>{realestate.name}</span>
                        <span className={styles.ChipNumber}>{realestate.number}</span>
                        {canRemoveRealestate && (
                            <button
                                type="button"
                                className={styles.RemoveIcon}
                                onClick={onRemoveRealestate(realestate)}
                                aria-label={t("screens.realestate.news_edit.remove_realestate", {
                                    name: realestate.name
                                }).toString()}
                            >
                                <UI.Icon icon={UI.SVGIcon.Close} size={10} />
                            </button>
                        )}
                    </span>
                ))}
            </div>
            {children && <div className={styles.Actions}>{children}</div>}
            {/* Without a realestate the empty message above already says it. */}
            {realestates.length > 0 && (
                <div className={styles.Hint}>
                    {t("screens.realestate.news_edit.realestates_hint", { count: realestates.length })}
                </div>
            )}
        </div>
    );
};

export const NewsRealestatesSection = observer(NewsRealestatesSectionBase);
